// App.tsx
import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { ConfigProvider, theme, App as AntdApp } from 'antd';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { LoginForm } from './shared/components/Login/LoginForm';
import { UserPanel } from './pages/UserPanel/UserPanel';
import { UsersTable } from './pages/Users/UsersTable';
import RequestsSection from './pages/UserPanel/RequestsSection';
import { RequestValidationPage, RequestDetailsPage } from './pages/RequestValidation';
import { WithdrawCompletionPage } from './pages/RequestValidation/WithdrawCompletionPage';
import { Wallet } from './pages/Wallet/Wallet';
import { Settings } from './pages/Settings/Settings';
import { Gamification } from './pages/Gamification/Gamification';
import { SuperAdminProvider } from './contexts/SuperAdminContext';
import { UserPanelProvider } from './contexts/UserPanelContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AdminSelectorProvider } from './contexts/AdminSelectorContext';
import { NotFound } from './pages/NotFound';
import { RouteTransition } from './components/RouteTransition';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { Analytics } from './pages/analytics/Analytics';
import { Audit } from './pages/Audit/Audit';

import { Operations } from './pages/Operations/Operations';
import GlobalStyles from './shared/styles/GlobalStyles';
import { useAuthStore, setGlobalQueryClient } from './shared/hooks/useAuth';
import { useTheme } from './contexts/ThemeContext';
import { PermissionRoute } from './shared/components/PermissionRoute';
import axios from 'axios';
import { API_BASE_URL } from './constants/config';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { preloadAppData } from './shared/stores/useStores';

import { getAndSanitize, sanitizeAndStore } from './shared/utils/sanitization';
import { NotificationProvider } from './contexts/NotificationContext';
import { SuperAdminRoute } from './shared/components/SuperAdminRoute';
import { useUserInitialization } from './shared/hooks/useUserInitialization';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      placeholderData: (previousData) => previousData
    }
  }
});

// Establecer referencia global del QueryClient para useAuth
setGlobalQueryClient(queryClient);

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const token = getAndSanitize('token');

  // NOTE: User status verification is handled by useUserInitialization hook in AppContent
  // PrivateRoute only checks for token existence - this prevents race conditions
  return token ? <>{children}</> : <Navigate to="/" />;
};

const AuthRoute: React.FC = () => {
  const token = getAndSanitize('token');
  const role = getAndSanitize('role');

  if (token && role) {
    switch (role) {
      case 'SUPERADMIN':
        // SuperAdmin now uses the same interface as Admin
        return <Navigate to="/user-panel" />;
      case 'SUPPORT':
        return <Navigate to="/support" />;
      default:
        return <Navigate to="/user-panel" />;
    }
  }
  return <LoginForm />;
};

const LoadingFallback: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100%',
    width: '100%'
  }}>
    Loading...
  </div>
);

const App: React.FC = () => {
  const { theme: currentTheme } = useTheme();

  return (
    <ConfigProvider
      theme={{
        algorithm: currentTheme === 'dark' 
          ? theme.darkAlgorithm 
          : theme.defaultAlgorithm,
        token: currentTheme === 'dark' 
          ? {
              colorPrimary: '#ac4949',
              colorBgBase: '#030303',
              colorText: '#ffffff',
              colorBgContainer: '#141414',
              colorBgElevated: '#1f1f1f',
              borderRadius: 6,
            }
          : {
              colorPrimary: '#ac4949',
              colorBgBase: '#f0f2f5',
              colorText: '#333333',
              colorBgContainer: '#ffffff',
              colorBgElevated: '#ffffff',
              borderRadius: 6,
            },
        components: currentTheme === 'dark' 
          ? {
              Layout: {
                headerBg: '#030303',
                bodyBg: '#0a0a0a',
                siderBg: '#030303',
              },
              Menu: {
                darkItemBg: '#030303',
                darkItemSelectedBg: '#1f1f1f',
              },
              Button: {
                defaultBg: '#1f1f1f',
                defaultColor: '#ffffff',
              },
            }
          : {
              Layout: {
                headerBg: '#ffffff',
                bodyBg: '#f0f2f5',
                siderBg: '#ffffff',
              },
              Menu: {
                itemBg: '#ffffff',
                itemSelectedBg: 'rgba(172, 73, 73, 0.1)',
              },
              Button: {
                defaultBg: '#ffffff',
                defaultColor: '#333333',
              },
            }
      }}
    >
      <AntdApp>
        <GlobalStyles />
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SocketProvider>
              <NotificationProvider>
                <AppContent />
                <div 
                  id="drawer-root" 
                  style={{ 
                    position: 'fixed',
                    zIndex: 1000,
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    overflow: 'visible',
                    pointerEvents: 'none'
                  }} 
                />
              </NotificationProvider>
            </SocketProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

// Componente interno que puede usar useNavigate() de manera segura
const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore(state => state.logout);
  const [retryCount, setRetryCount] = useState<Record<string, number>>({});
  const MAX_RETRIES = 2;
  const { message: contextMessage } = AntdApp.useApp();

  // Initialize user data and start periodic monitoring
  useUserInitialization();

  // Preload app data on initialization
  useEffect(() => {
    // Preload app data once when the app starts and user is authenticated
    const token = getAndSanitize('token');
    if (token) {
      preloadAppData().then(success => {
        if (success) {
        }
      });
    }
  }, []);
  
  // Función para renovar el token
  const attemptTokenRefresh = useCallback(async () => {
    try {
      const token = getAndSanitize('token');
      if (!token) return false;
      
      // Intentar renovar el token con el endpoint correspondiente
const response = await axios.post(`${API_BASE_URL}/api/auth/token/refresh`, {}, {
  headers: { Authorization: `Bearer ${token}` }
});
      
      if (response.data?.success && response.data?.data?.token) {
        let newToken = response.data.data.token;
        // Strip "Bearer" prefix if present (backend might return it with "Bearer")
        newToken = newToken.replace(/^Bearer\s+/i, '');
        sanitizeAndStore('token', newToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[Auth] Error al renovar token:', error);
      return false;
    }
  }, []);
  
  // Agregar un interceptor global para manejar errores de autenticación
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        // Obtener información del error para identificar la solicitud
        const requestId = error.config?.url || 'unknown';
        const currentRetries = retryCount[requestId] || 0;
        
        // Verificar si es un error de autenticación y si podemos reintentar
        if (error.response?.status === 401 && currentRetries < MAX_RETRIES) {
          // Actualizar contador de reintentos para esta URL
          setRetryCount(prev => ({
            ...prev,
            [requestId]: currentRetries + 1
          }));
          
          // Intentar renovar el token
          const refreshSuccess = await attemptTokenRefresh();
          
          if (refreshSuccess && error.config) {
            // Si se renovó correctamente, reintentar la solicitud original
            const token = getAndSanitize('token');
            
            // Crear una nueva solicitud en lugar de modificar la existente
            try {
              const originalRequest = error.config;

              // Clean headers to avoid double "Bearer" prefix
              // Axios stores headers in a nested structure (common, get, post, etc.)
              // We need to create a clean headers object with only the new Authorization
              const cleanHeaders: Record<string, string> = {
                'Content-Type': 'application/json'
              };

              // Copy other non-Authorization headers from original request if needed
              if (originalRequest.headers) {
                const headersToCopy = ['content-type', 'accept'];
                headersToCopy.forEach(key => {
                  const value = (originalRequest.headers as any)[key];
                  if (value && key !== 'authorization') {
                    cleanHeaders[key] = value;
                  }
                });
              }

              // Set the new Authorization header
              cleanHeaders['Authorization'] = `Bearer ${token}`;

              // Crear una nueva solicitud con headers limpios
              return await axios({
                method: originalRequest.method,
                url: originalRequest.url,
                data: originalRequest.data,
                params: originalRequest.params,
                headers: cleanHeaders,
                withCredentials: originalRequest.withCredentials
              });
            } catch (retryError) {
              console.error('[Auth] Error al reintentar después de renovar token:', retryError);
            }
          }
        }
        
        // Si llegamos a este punto, verificar si es un error de autenticación definitivo
        if (error.response?.status === 401) {
          const errorData = error.response?.data as any;
          const isAuthError = 
            errorData?.code === 'TOKEN_EXPIRED' || 
            errorData?.code === 'INVALID_TOKEN' ||
            errorData?.code === 'USER_INACTIVE' ||
            errorData?.code === 'NO_TOKEN' ||
            error.response?.statusText === 'Unauthorized';
            
          if (isAuthError) {
            // Mostrar un mensaje apropiado basado en el tipo de error
            let errorMessage = t('common.sessionExpired');
            if (errorData?.code === 'USER_INACTIVE') {
              errorMessage = t('Tu cuenta ha sido desactivada. Contacta al administrador.');
            }
            
            contextMessage.error(errorMessage);
            logout();
            navigate('/');
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, logout, t, retryCount, attemptTokenRefresh, contextMessage]);

  return (
    <>
    <Routes>
      <Route path="/" element={<AuthRoute />} />
      {/* SuperAdmin route removed - SuperAdmin now uses /user-panel */}
      {/* Legacy super-admin route redirects to user-panel */}
      <Route
        path="/super-admin"
        element={<Navigate to="/user-panel" replace />}
      />

      <Route
        path="/user-panel"
        element={
          <PrivateRoute>
            <SuperAdminProvider>
              <AdminSelectorProvider>
                <UserPanelProvider>
                  <UserPanel />
                </UserPanelProvider>
              </AdminSelectorProvider>
            </SuperAdminProvider>
          </PrivateRoute>
        }
      >
        <Route 
          index
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <Navigate to="/user-panel/dashboard" />
              </RouteTransition>
            </Suspense>
          }
        />
        <Route 
          path="dashboard" 
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <Dashboard />
              </RouteTransition>
            </Suspense>
          }
        />
        
        {/* Ruta protegida para Users */}
        <Route element={<PermissionRoute section="users" fallbackPath="/user-panel/dashboard" />}>
          <Route
            path="users"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <RouteTransition>
                  <UsersTable />
                </RouteTransition>
              </Suspense>
            }
          />
        </Route>

        {/* Ruta protegida para Operations */}
        <Route element={<PermissionRoute section="operations" fallbackPath="/user-panel/dashboard" />}>
          <Route 
            path="operations" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <RouteTransition>
                  <Operations />
                </RouteTransition>
              </Suspense>
            } 
          />
        </Route>
        
        <Route
          path="requests/:requestId/validate"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <RequestValidationPage />
              </RouteTransition>
            </Suspense>
          }
        />
        <Route
          path="requests/:requestId/details"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <RequestDetailsPage />
              </RouteTransition>
            </Suspense>
          }
        />
        <Route
          path="requests/:requestId/withdraw"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <WithdrawCompletionPage />
              </RouteTransition>
            </Suspense>
          }
        />
        <Route
          path="requests"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <RequestsSection />
              </RouteTransition>
            </Suspense>
          }
        />
        
        {/* Ruta protegida para Wallet */}
        <Route element={<PermissionRoute section="wallet" fallbackPath="/user-panel/dashboard" />}>
          <Route
            path="wallet"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <RouteTransition>
                  <Wallet />
                </RouteTransition>
              </Suspense>
            }
          />
        </Route>

        <Route
          path="gamification"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <Gamification />
              </RouteTransition>
            </Suspense>
          }
        />

        <Route
          path="settings"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RouteTransition>
                <Settings />
              </RouteTransition>
            </Suspense>
          }
        />

        {/* Analytics - SuperAdmin only */}
        <Route
          path="analytics"
          element={
            <SuperAdminRoute>
              <Suspense fallback={<LoadingFallback />}>
                <RouteTransition>
                  <AnalyticsProvider>
                    <Analytics />
                  </AnalyticsProvider>
                </RouteTransition>
              </Suspense>
            </SuperAdminRoute>
          }
        />

        {/* Audit - SuperAdmin only */}
        <Route
          path="audit"
          element={
            <SuperAdminRoute>
              <Suspense fallback={<LoadingFallback />}>
                <RouteTransition>
                  <Audit />
                </RouteTransition>
              </Suspense>
            </SuperAdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
};

const AppWithTheme: React.FC = () => {
  return (
    <ThemeProvider>
      <App />
    </ThemeProvider>
  );
};

export default AppWithTheme;